// meet — pure utilities. Row-shape transforms used by fetch blocks (allowed
// imports per the chat pattern), bucket constants, and the room-mode helper.

// Source of truth for the four AI suggestion buckets. Used by:
//   - meet-analyze Edge Function (the LLM prompt + responseSchema)
//   - meet_transcript_analyses.suggestions JSONB keys
//   - applySuggestion dispatcher
//   - SuggestionCard rendering
// Adding a fifth bucket is a one-line change here + the Edge Function schema.
export const SUGGESTION_BUCKETS = {
  calendar_events: { label: 'Calendar',       viewLabel: 'View event →',   stateKey: 'selectedEventId',    applyFn: 'saveEventToSupabase',  route: '/calendar' },
  tickets:         { label: 'Tickets',        viewLabel: 'View ticket →',  stateKey: 'selectedTicketId',   applyFn: 'saveTicketToSupabase', route: '/tickets' },
  notes:           { label: 'Notes',          viewLabel: 'View note →',    stateKey: 'selectedNoteId',     applyFn: 'createNote',           route: '/documents/notes' },
  knowledge_base:  { label: 'Knowledge base', viewLabel: 'View article →', stateKey: 'selectedDocumentId', applyFn: 'saveKbArticle',        route: '/documents/kb' },
  chat_messages:   { label: 'Chat',           viewLabel: 'Open chat →',                                    applyFn: 'messageUser',          route: '/chat' }
}

// Mode is derived from participant count — never persist it as state.
// dm < 3 < grid < 13 ≤ stage. Used by VideoRoom layout AND livekitConnect's
// simulcast layer choice.
export const getRoomMode = (participantCount) => {
  if (participantCount < 3) return 'dm'
  if (participantCount < 13) return 'grid'
  return 'stage'
}

export const rowToRoom = (r) => ({
  id: r.id,
  name: r.name || 'Untitled',
  topic: r.topic || '',
  kind: r.kind || 'room',
  isPrivate: !!r.is_private,
  transcribeEnabled: !!r.transcribe_enabled,
  livekitInitialized: !!r.livekit_initialized,
  createdBy: r.created_by,
  createdAt: r.created_at,
  endedAt: r.ended_at,
  calendarEventId: r.calendar_event_id || null,
  chatChannelId: r.chat_channel_id || null,
  // Guest-join requirements (migration 0019). `guestRequireName` defaults
  // to true; the others default false/null. Read by NewRoomModal /
  // MeetRoomSettingsModal / /meet-guest page.
  guestRequireName: r.guest_require_name !== false,
  guestRequireEmail: !!r.guest_require_email,
  guestPassword: r.guest_password || null,
  guestAutoAdmit: !!r.guest_auto_admit
})

export const rowToParticipant = (r) => ({
  id: r.id,
  roomId: r.room_id,
  userId: r.user_id,
  identity: r.identity,
  joinedAt: r.joined_at,
  leftAt: r.left_at
})

export const rowToUtterance = (r) => ({
  id: r.id,
  roomId: r.room_id,
  userId: r.user_id,
  // The transcriber writes `speaker_name` as whatever LiveKit's
  // `participant.name` is, which in our case is the user's EMAIL (set by
  // meet-token when minting the token). Keep it stored as-is; components
  // resolve the display name via root.people at render time.
  speakerEmail: r.speaker_name || '',
  text: r.text || '',
  startedAt: r.started_at,
  endedAt: r.ended_at,
  confidence: r.confidence
})

// Resolve an email OR a free-form speaker string to a human display name
// by looking up root.people (populated via peopleFetch from shared-gov).
// Falls back to the input if no match.
export const speakerDisplayName = (root, speakerEmail) => {
  if (!speakerEmail) return 'Unknown'
  const people = root?.people || []
  const match = people.find(p => p.email === speakerEmail)
  return match?.name || speakerEmail
}

// Find the routing root by walking up the parent chain to the first element
// that owns the `routes` prop (i.e. the Page element that mounts pages/index).
// Needed because DOMQL v3.14 doesn't expose `el.getRoot()` — existing components
// like AppNavbar use this walk pattern, and calling `el.router(path, undef)`
// throws `Cannot read properties of undefined (reading 'context')`.
export const routeTo = (el, path) => {
  if (!el || !path) return
  let root = el
  while (root && !root.routes) root = root.parent
  if (root) el.router(path, root)
}

// Content-addressable id for a suggestion item — used by applied_items so
// regenerating the analysis doesn't break checkmarks. Truncated sha256 of
// title + description; collision risk is negligible at the scales involved.
export const fingerprintSuggestion = async (item) => {
  const text = `${item.title || ''}|${item.description || item.content || item.body || ''}`
  const buf = new TextEncoder().encode(text)
  const hash = await crypto.subtle.digest('SHA-256', buf)
  const bytes = new Uint8Array(hash).slice(0, 8)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Strip applied_items entries whose fingerprints don't appear in the current
// suggestions blob. Called by applySuggestion before appending the new entry
// so the array stays bounded by the most recent analysis, not lifetime clicks.
export const pruneStaleApplied = async (appliedItems, suggestions) => {
  if (!appliedItems?.length) return []
  const liveFingerprints = new Set()
  for (const bucket of Object.keys(SUGGESTION_BUCKETS)) {
    for (const item of (suggestions?.[bucket] || [])) {
      liveFingerprints.add(await fingerprintSuggestion(item))
    }
  }
  return appliedItems.filter((entry) => liveFingerprints.has(entry.fingerprint))
}
